<!--
  Funnel diagnostics (RF-21, AC-10). Hyper table + progress bars; no chart library is
  pulled in for what is essentially two ordered lists.
-->
<div class="card">
  <div class="card-body">
    <div class="d-flex justify-content-between align-items-start mb-3">
      <div>
        <h4 class="header-title mb-1">{{ 'kanban.funnelOverview.title' | translate }}</h4>
        <p class="text-muted font-13 mb-0">{{ 'kanban.funnelOverview.subtitle' | translate }}</p>
      </div>

      <!--
        The period is the BOARD's period (SPEC-2026-10 RF-08): the toggle shows the range in use,
        and a preset fills the board's filter — figures, conversion cohort and column headers move
        together.
      -->
      <div class="dropdown">
        <a
          href="javascript:void(0);"
          class="dropdown-toggle btn btn-sm btn-light"
          data-bs-toggle="dropdown"
          [attr.aria-label]="'kanban.pipelineSummary.selectPeriod' | translate"
        >
          <i class="ri-calendar-line me-1"></i>
          {{ periodLabel }}
        </a>
        <div class="dropdown-menu dropdown-menu-end">
          @for (option of periods; track option.value) {
            <a
              href="javascript:void(0);"
              class="dropdown-item"
              (click)="selectPreset(option.value)"
            >
              {{ option.label | translate }}
            </a>
          }
        </div>
      </div>
    </div>

    <!--
      Open / won / lost for the period (RF-01, RF-02, RF-04).

      These moved here from a permanent block above the board on 2026-08-18: the CRM
      dashboard already shows the same three numbers with the same variation, so a second
      always-visible copy was two sources of truth for one figure. Inside the diagnostic
      they set the context for the conversion table below.
    -->
    @if (summary) {
      <div class="row mb-2">
        <div class="col-md-4">
          <div class="d-flex align-items-center">
            <div class="avatar-sm rounded-circle bg-soft-primary d-flex align-items-center justify-content-center flex-shrink-0">
              <i class="ri-folder-open-line font-20 text-primary"></i>
            </div>
            <div class="flex-grow-1 ms-2">
              <h5 class="mt-0 mb-0">
                {{ summary.open.count }}
                <small class="text-muted fw-normal">
                  · {{ summary.open.value | currency: 'BRL' : 'symbol-narrow' : '1.0-0' }}
                </small>
              </h5>
              <p class="text-muted mb-0 font-12">
                {{ 'kanban.pipelineSummary.open' | translate }}
                @if (hasVariation(summary.open)) {
                  <span class="ms-1" [ngClass]="'text-' + variationContext(summary.open.countVariation)">
                    <i [class]="variationIcon(summary.open.countVariation)"></i>
                    {{ summary.open.countVariation }}%
                  </span>
                } @else {
                  <span class="ms-1 text-muted" [matTooltip]="'kanban.pipelineSummary.noBaseline' | translate">—</span>
                }
              </p>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="d-flex align-items-center">
            <div class="avatar-sm rounded-circle bg-soft-success d-flex align-items-center justify-content-center flex-shrink-0">
              <i class="ri-trophy-line font-20 text-success"></i>
            </div>
            <div class="flex-grow-1 ms-2">
              <h5 class="mt-0 mb-0">
                {{ summary.won.count }}
                <small class="text-muted fw-normal">
                  · {{ summary.won.value | currency: 'BRL' : 'symbol-narrow' : '1.0-0' }}
                </small>
              </h5>
              <p class="text-muted mb-0 font-12">
                {{ 'kanban.pipelineSummary.won' | translate }}
                @if (hasVariation(summary.won)) {
                  <span class="ms-1" [ngClass]="'text-' + variationContext(summary.won.countVariation)">
                    <i [class]="variationIcon(summary.won.countVariation)"></i>
                    {{ summary.won.countVariation }}%
                  </span>
                } @else {
                  <span class="ms-1 text-muted">—</span>
                }
              </p>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="d-flex align-items-center">
            <div class="avatar-sm rounded-circle bg-soft-danger d-flex align-items-center justify-content-center flex-shrink-0">
              <i class="ri-close-circle-line font-20 text-danger"></i>
            </div>
            <div class="flex-grow-1 ms-2">
              <h5 class="mt-0 mb-0">
                {{ summary.lost.count }}
                <small class="text-muted fw-normal">
                  · {{ summary.lost.value | currency: 'BRL' : 'symbol-narrow' : '1.0-0' }}
                </small>
              </h5>
              <p class="text-muted mb-0 font-12">
                {{ 'kanban.pipelineSummary.lost' | translate }}
                @if (hasVariation(summary.lost)) {
                  <!-- Inverted: more lost deals reads as bad, not as growth. -->
                  <span class="ms-1" [ngClass]="'text-' + variationContext(summary.lost.countVariation, true)">
                    <i [class]="variationIcon(summary.lost.countVariation)"></i>
                    {{ summary.lost.countVariation }}%
                  </span>
                } @else {
                  <span class="ms-1 text-muted">—</span>
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      <hr class="my-3" />
    }

    @if (loading) {
      <div class="text-center py-3">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">{{ 'kanban.funnelOverview.loading' | translate }}</span>
        </div>
      </div>
    } @else if (!overview || overview.stages.length === 0) {
      <div class="alert alert-secondary mb-0" role="alert">
        <i class="ri-information-line me-1"></i>
        {{ 'kanban.funnelOverview.empty' | translate }}
      </div>
    } @else {
      <div class="table-responsive">
        <table class="table table-centered table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th>{{ 'kanban.funnelOverview.stage' | translate }}</th>
              <th class="text-center">{{ 'kanban.funnelOverview.dealsReached' | translate }}</th>
              <th class="text-center">{{ 'kanban.funnelOverview.conversionToNext' | translate }}</th>
              <th class="text-center">{{ 'kanban.funnelOverview.averageTime' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @for (stage of overview.stages; track stage.stage) {
              <tr>
                <td class="fw-semibold">{{ stage.name }}</td>
                <td class="text-center">{{ stage.dealsReached }}</td>
                <td class="text-center">
                  @if (hasConversion(stage)) {
                    <span class="badge" [ngClass]="'bg-soft-' + conversionContext(stage) + ' text-' + conversionContext(stage)">
                      {{ stage.conversionToNext }}%
                    </span>
                  } @else {
                    <!-- Last stage, or nobody reached it: a dash, never 0% (AC-10). -->
                    <span class="text-muted" [matTooltip]="'kanban.funnelOverview.noConversionTooltip' | translate">—</span>
                  }
                </td>
                <td class="text-center">
                  {{ formatDuration(stage.averageTimeInStageMs) }}
                  @if (stage.averageTimeInStageSamples > 0) {
                    <br />
                    <small class="text-muted">
                      {{ 'kanban.funnelOverview.samples' | translate: { count: stage.averageTimeInStageSamples } }}
                    </small>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!--
        E3 RF-27: how the deals lost in the period divide across loss reasons — the company's
        rows and the platform discards side by side, the latter visibly distinct (ADR-001).
      -->
      <h4 class="header-title mt-4 mb-1">{{ 'kanban.funnelOverview.lossReasonsTitle' | translate }}</h4>
      <p class="text-muted font-13 mb-3">
        {{ 'kanban.funnelOverview.lossReasonsSubtitle' | translate: { total: totalLost } }}
      </p>

      @if (!overview.lossReasons?.length) {
        <p class="text-muted font-13 mb-0">{{ 'kanban.funnelOverview.lossReasonsEmpty' | translate }}</p>
      } @else {
        <div class="table-responsive">
          <table class="table table-centered table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>{{ 'kanban.funnelOverview.lossReason' | translate }}</th>
                <th class="text-center">{{ 'kanban.funnelOverview.lostDeals' | translate }}</th>
                <th class="text-end">{{ 'kanban.funnelOverview.lostValue' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (row of overview.lossReasons; track lossReasonTrack(row)) {
                <tr>
                  <td class="fw-semibold">
                    {{ lossReasonLabel(row) }}
                    @if (row.platform) {
                      <span class="badge bg-soft-secondary text-muted ms-1 font-11">
                        {{ 'kanban.lossReason.platformBadge' | translate }}
                      </span>
                    }
                  </td>
                  <td class="text-center">{{ row.count }}</td>
                  <td class="text-end">{{ row.value | currency: 'BRL' : 'symbol-narrow' : '1.0-0' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      <h4 class="header-title mt-4 mb-1">{{ 'kanban.funnelOverview.ageTitle' | translate }}</h4>
      <p class="text-muted font-13 mb-3">
        {{ 'kanban.funnelOverview.ageSubtitle' | translate: { total: totalOpen } }}
      </p>

      @for (band of overview.ageDistribution; track band.band) {
        <div class="mb-2">
          <div class="d-flex justify-content-between font-13 mb-1">
            <span>{{ ageBandLabel(band.band) | translate }}</span>
            <span class="text-muted">
              {{ band.count }} ·
              {{ band.value | currency: 'BRL' : 'symbol-narrow' : '1.0-0' }}
            </span>
          </div>
          <div class="progress age-progress">
            <div
              class="progress-bar bg-primary"
              role="progressbar"
              [style.width.%]="ageBarPercent(band.count)"
              [attr.aria-valuenow]="band.count"
              aria-valuemin="0"
              [attr.aria-valuemax]="totalOpen"
              [attr.aria-label]="ageBandLabel(band.band) | translate"
            ></div>
          </div>
        </div>
      }
    }
  </div>
</div>
